import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Banner, Button, Field } from "../ui/Field";

export default function Login() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const options = await api.post<Parameters<typeof startAuthentication>[0]["optionsJSON"]>("/api/auth/login/options", { email });
      const assertion = await startAuthentication({ optionsJSON: options });
      await api.post("/api/auth/login/verify", { email, assertion });
      await refresh();
      nav("/sites");
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-stretch bg-[var(--color-paper)] bg-paper-noise">
      {/* Left — brand panel */}
      <aside className="hidden md:flex md:w-[44%] lg:w-[40%] bg-[var(--color-ink)] text-[var(--color-paper)] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] bg-hatch" />
        <div className="relative flex flex-col justify-between p-12 lg:p-16 w-full">
          <header className="flex items-center justify-between">
            <div className="inline-flex items-baseline gap-1.5">
              <span className="display text-[42px] leading-none">CMS</span>
              <span className="text-[var(--color-accent)] font-mono text-[22px]">+</span>
            </div>
            <div className="section-no text-[var(--color-mute-2)]">№ 001 · Login</div>
          </header>

          <div>
            <p className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--color-mute-2)] mb-4">Vol. 1 — Editorial Software</p>
            <h1 className="display text-5xl lg:text-6xl leading-[1.02] text-[var(--color-paper)] max-w-md">
              The headless CMS, <em>built</em> for the way you publish.
            </h1>
            <p className="mt-6 max-w-sm text-[14px] text-[var(--color-mute-2)] leading-relaxed">
              Pages, collections, snippets, assets, forms — all on Cloudflare's edge. Browser-rendered builds, deployed in seconds.
            </p>
          </div>

          <footer className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--color-mute-2)] flex items-center justify-between">
            <span>Workers · D1 · R2 · KV · Pages</span>
            <span>cms-plus / v1</span>
          </footer>
        </div>
      </aside>

      {/* Right — form */}
      <section className="flex-1 flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm reveal">
          <div className="mb-8">
            <div className="section-no mb-3">№ Sign in</div>
            <h2 className="display text-4xl mb-2">Welcome back.</h2>
            <p className="text-[13.5px] text-[var(--color-muted)] leading-relaxed">Authenticate with your passkey. We don't keep passwords here.</p>
          </div>

          {err && <Banner kind="error">{err}</Banner>}

          <form onSubmit={onSubmit} className="reveal reveal-1">
            <Field
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              autoComplete="username webauthn"
              placeholder="you@yourcompany.com"
            />
            <Button type="submit" disabled={busy} size="lg" className="w-full mt-2">
              {busy ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-paper)] pulse-dot mr-2" />
                  Awaiting passkey…
                </>
              ) : (
                <>Sign in with passkey →</>
              )}
            </Button>
          </form>

          <hr className="rule" />

          <div className="text-[12px] text-[var(--color-muted)] leading-relaxed">
            New here? Ask a workspace admin to send you an invite. The link will register your first passkey.
          </div>
        </div>
      </section>
    </div>
  );
}
